import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useEstornarBaixa, useMarcarPaga } from '@/hooks/useCobrancas';
import type { Cobranca, FormaBaixa } from '@/api/endpoints/cobrancas';
import { FORMA_BAIXA_ROTULO, reaisDeCentavos } from './formato';

const campo =
  'w-full rounded-lg border border-border-strong bg-muted px-3 py-2 text-sm text-text-title focus:border-primary focus:outline-none';
const rotulo = 'mb-1.5 block text-xs font-medium text-text-secondary';

const hojeISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

function mensagemDeErro(error: unknown, padrao: string) {
  const e = error as { response?: { data?: { message?: string } } };
  return e?.response?.data?.message || padrao;
}

/** Marcar a cobrança como paga quando o dinheiro chegou por fora. */
export function MarcarPagaForm({
  c,
  onFeito,
  onVoltar,
}: {
  c: Cobranca;
  onFeito: () => void;
  onVoltar: () => void;
}) {
  const marcar = useMarcarPaga();
  const [forma, setForma] = useState<FormaBaixa>('pix_direto');
  const [dia, setDia] = useState(hojeISO());
  const [observacao, setObservacao] = useState('');
  const [erro, setErro] = useState<string | null>(null);

  const confirmar = () => {
    setErro(null);
    if (observacao.trim().length < 3) {
      setErro('Explique como foi recebido.');
      return;
    }
    // Meio-dia local: a data não muda de dia com o fuso.
    const pagaEm = new Date(`${dia}T12:00:00`).toISOString();
    marcar.mutate(
      { id: c.id, dados: { forma, pagaEm, observacao: observacao.trim() } },
      { onSuccess: onFeito, onError: (e) => setErro(mensagemDeErro(e, 'Não deu para dar baixa.')) }
    );
  };

  return (
    <div className="rounded-xl border border-border bg-muted/40 p-4">
      <p className="text-sm font-medium text-text-title">
        Marcar {reaisDeCentavos(c.valor_centavos)} como recebido
      </p>
      <p className="mt-1 text-xs text-text-secondary">
        {c.plano_nome ? 'O plano é concedido na hora e o produtor recebe a confirmação. ' : ''}
        Entra em Finanças como baixa manual, sem taxa.
      </p>
      <div className="mt-3 grid grid-cols-2 gap-3 max-sm:grid-cols-1">
        <div>
          <label htmlFor="baixa-forma" className={rotulo}>
            Como recebeu
          </label>
          <select
            id="baixa-forma"
            className={campo}
            value={forma}
            onChange={(e) => setForma(e.target.value as FormaBaixa)}
          >
            {Object.entries(FORMA_BAIXA_ROTULO).map(([valor, texto]) => (
              <option key={valor} value={valor}>
                {texto}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="baixa-dia" className={rotulo}>
            Data do recebimento
          </label>
          <input
            id="baixa-dia"
            type="date"
            max={hojeISO()}
            className={campo}
            value={dia}
            onChange={(e) => setDia(e.target.value)}
          />
        </div>
      </div>
      <div className="mt-3">
        <label htmlFor="baixa-obs" className={rotulo}>
          Observação
        </label>
        <textarea
          id="baixa-obs"
          rows={2}
          className={campo}
          value={observacao}
          onChange={(e) => setObservacao(e.target.value)}
          placeholder="Ex.: Pix na conta da empresa em 22/10"
          aria-invalid={Boolean(erro)}
        />
      </div>
      {erro && <p className="mt-2 text-sm text-error">{erro}</p>}
      <div className="mt-3 flex flex-wrap gap-2">
        <Button type="button" size="sm" onClick={confirmar} disabled={marcar.isPending}>
          {marcar.isPending && <Loader2 size={16} className="animate-spin" />}
          Confirmar recebimento
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onVoltar}
          disabled={marcar.isPending}
        >
          Voltar
        </Button>
      </div>
    </div>
  );
}

/** Desfazer uma baixa manual feita por engano. */
export function EstornarBaixaForm({
  c,
  onFeito,
  onVoltar,
}: {
  c: Cobranca;
  onFeito: () => void;
  onVoltar: () => void;
}) {
  const estornar = useEstornarBaixa();
  const [observacao, setObservacao] = useState('');
  const [erro, setErro] = useState<string | null>(null);

  const confirmar = () => {
    setErro(null);
    if (observacao.trim().length < 3) {
      setErro('Explique por que a baixa está sendo desfeita.');
      return;
    }
    estornar.mutate(
      { id: c.id, observacao: observacao.trim() },
      {
        onSuccess: onFeito,
        onError: (e) => setErro(mensagemDeErro(e, 'Não deu para estornar a baixa.')),
      }
    );
  };

  return (
    <div className="rounded-xl border border-error/40 bg-error/5 p-4">
      <p className="text-sm font-medium text-text-title">Desfazer a baixa manual?</p>
      <p className="mt-1 text-xs text-text-secondary">
        A cobrança volta a esperar pagamento (o link continua valendo), o período concedido é
        desfeito e o valor sai de Finanças.
      </p>
      <div className="mt-3">
        <label htmlFor="estorno-obs" className={rotulo}>
          Motivo
        </label>
        <textarea
          id="estorno-obs"
          rows={2}
          className={campo}
          value={observacao}
          onChange={(e) => setObservacao(e.target.value)}
          placeholder="Ex.: marquei a cobrança errada"
          aria-invalid={Boolean(erro)}
        />
      </div>
      {erro && <p className="mt-2 text-sm text-error">{erro}</p>}
      <div className="mt-3 flex flex-wrap gap-2">
        <Button
          type="button"
          variant="danger"
          size="sm"
          onClick={confirmar}
          disabled={estornar.isPending}
        >
          {estornar.isPending && <Loader2 size={16} className="animate-spin" />}
          Desfazer baixa
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onVoltar}
          disabled={estornar.isPending}
        >
          Voltar
        </Button>
      </div>
    </div>
  );
}
